/**
 * TanStack Query hooks для Media endpoints (ТЗ 2026-09-22).
 *
 * - Режим 1 «Поиск фото»: estimate + start + status/results/cancel.
 * - Режим 2 «Генерация картинок»: styles + estimate + start.
 * - Статус опрашивается пока прогон queued/running; прогресс — строки.
 * - Ошибки — унифицированный {error:{message}} (extractApiError на странице).
 */
import { useMutation, useQuery } from '@tanstack/react-query';

import api from '@/lib/api';

export type TierKey = 'economy' | 'optimal' | 'max';
export type MediaMode = 'image_search' | 'image_generation';
export type MediaRunStatusValue =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface MediaRunStatus {
  run_id: string;
  status: MediaRunStatusValue;
  is_test_run: boolean;
  total_rows: number;
  processed_rows: number;
  success_rows: number;
  error_rows: number;
  estimated_credits: number;
  spent_credits: number;
  reserved_credits: number;
  error_message: string | null;
  progress: string[];
}

export interface MediaSearchEstimate {
  group_count: number;
  source_queries_upper: number;
  source_cost_credits: number;
  vision_cost_credits_upper: number;
  total_credits_upper: number;
  per_group_credits_upper: number;
  candidate_depth: number;
  max_photos: number;
  pricing: string;
}

export interface MediaGenEstimate {
  total_rows: number;
  images_total_upper: number;
  total_credits: number;
  per_image_credits: number;
  images_per_row: number;
  pricing: string;
}

export interface MediaStyle {
  name: string;
  description: string;
}

export interface MediaStylesResponse {
  styles: MediaStyle[];
  command_buttons: string[];
  images_per_row: number;
  image_size: string;
  prompt_template: string;
}

export interface MediaPhotoEntry {
  column: string;
  asset_id: string;
  source_url: string | null;
  width: number | null;
  height: number | null;
}

export interface MediaResultRow {
  row_index: number;
  row_status: string | null;
  columns: string[];
  search_mode: string | null;
  mode: string | null;
  photos: MediaPhotoEntry[];
  raw_preview: string;
}

export interface MediaResultsResponse {
  run_id: string;
  status: MediaRunStatusValue;
  is_test_run: boolean;
  total_rows: number;
  processed_rows: number;
  success_rows: number;
  error_rows: number;
  spent_credits: number;
  rows: Record<string, MediaResultRow>;
}

export interface MediaStartBody {
  dataset_id: string;
  tier_key: TierKey;
  is_test_run: boolean;
  test_run_limit?: number;
  request_id?: string;
  user_note?: string;
  user_text?: string;
  style?: string;
  custom_style?: string;
  selected_columns?: string[];
  image_count?: number;
}

// === Status / Results ===

const ACTIVE_STATUSES: MediaRunStatusValue[] = ['queued', 'running'];

export function useMediaRunStatus(runId: string | null) {
  return useQuery<MediaRunStatus>({
    queryKey: ['media', 'run', runId, 'status'],
    enabled: !!runId,
    refetchInterval: (q) => {
      const st = q.state.data?.status;
      return st && ACTIVE_STATUSES.includes(st) ? 2500 : false;
    },
    queryFn: async () => {
      const r = await api.get<MediaRunStatus>(`/media/run/${runId}/status`);
      return r.data;
    },
  });
}

export function useMediaRunResults(runId: string | null) {
  return useQuery<MediaResultsResponse>({
    queryKey: ['media', 'run', runId, 'results'],
    enabled: !!runId,
    refetchInterval: (q) => {
      const st = q.state.data?.status;
      return st && ACTIVE_STATUSES.includes(st) ? 5000 : false;
    },
    queryFn: async () => {
      const r = await api.get<MediaResultsResponse>(`/media/run/${runId}/results`);
      return r.data;
    },
  });
}

export function useMediaStyles(tierKey: TierKey, enabled: boolean) {
  return useQuery<MediaStylesResponse>({
    queryKey: ['media', 'styles', tierKey],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const r = await api.get<MediaStylesResponse>('/media/generation/styles', {
        params: { tier_key: tierKey },
      });
      return r.data;
    },
  });
}

function estimateBody(body: MediaStartBody) {
  const { dataset_id, tier_key, is_test_run, test_run_limit, user_note, user_text } = body;
  return { dataset_id, tier_key, is_test_run, test_run_limit, user_note, user_text };
}

export function useMediaSearchEstimate(body: MediaStartBody | null) {
  return useQuery<MediaSearchEstimate>({
    queryKey: ['media', 'estimate', 'search', body?.dataset_id, body?.tier_key, body?.is_test_run],
    enabled: !!body?.dataset_id,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await api.post<MediaSearchEstimate>(
        '/media/image-search/estimate',
        estimateBody(body!),
      );
      return r.data;
    },
  });
}

export function useMediaGenEstimate(body: MediaStartBody | null) {
  return useQuery<MediaGenEstimate>({
    queryKey: ['media', 'estimate', 'gen', body?.dataset_id, body?.tier_key, body?.is_test_run],
    enabled: !!body?.dataset_id,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await api.post<MediaGenEstimate>(
        '/media/generation/estimate',
        estimateBody(body!),
      );
      return r.data;
    },
  });
}

export function useMediaSearchStart() {
  return useMutation({
    mutationFn: async (body: MediaStartBody) => {
      const r = await api.post<MediaRunStatus>('/media/image-search/start', body);
      return r.data;
    },
  });
}

export function useMediaGenStart() {
  return useMutation({
    mutationFn: async (body: MediaStartBody) => {
      const r = await api.post<MediaRunStatus>('/media/generation/start', body);
      return r.data;
    },
  });
}

export function useMediaRunCancel() {
  return useMutation({
    mutationFn: async (runId: string) => {
      const r = await api.post<MediaRunStatus>(`/media/run/${runId}/cancel`);
      return r.data;
    },
  });
}

export async function fetchMediaAssetBlob(assetId: string): Promise<string> {
  const r = await api.get(`/media/asset/${assetId}/file`, { responseType: 'blob' });
  return URL.createObjectURL(r.data as Blob);
}
